/**
 * Step 3's slug seed — the one place a portal slug is derived, varied and
 * shaped into the host/example strings the address step renders.
 *
 * Why this is a module and not four template literals in signup.tsx:
 * every step-3 field that reads as "your address" is DERIVED from the
 * company name, and the stale-seed finding was that they didn't all reflow
 * after a company-name edit on step 2. Derivation living in one pure module
 * is what makes "they reflow together" testable without a DOM — the
 * dispatcher's vitest is node-env only (see site-studio.test.ts for the
 * same shape).
 *
 * Nothing here talks to the network on its own: pickAvailableSlug takes the
 * availability check as an argument so it stays pure-ish and testable, and
 * so there is exactly ONE availability implementation — the caller passes
 * authService.slugCheck, the same endpoint the live field already uses.
 */

/** Mirrors signupSlugSchema in @breaker/shared-schemas. */
export const SLUG_MIN = 3;
export const SLUG_MAX = 40;
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Does this value satisfy signupSlugSchema's shape rules? */
export function isSlugShaped(value: string): boolean {
  return value.length >= SLUG_MIN && value.length <= SLUG_MAX && SLUG_PATTERN.test(value);
}

/** Company name → a first-guess portal slug the user can edit. */
export function suggestSlug(companyName: string): string {
  return companyName
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_MAX);
}

/**
 * The nth alternative for a taken base: `ace-haulage` → `ace-haulage-2`.
 *
 * The suffix must fit inside SLUG_MAX, so a long base is trimmed to make
 * room — and then re-stripped of trailing hyphens, because `ace-` + `-2`
 * would be `ace--2`, which the doubled-hyphen rule in signupSlugSchema
 * rejects. Getting this wrong produces a pre-filled slug the server 400s.
 */
export function slugVariant(base: string, n: number): string {
  const suffix = `-${n}`;
  const room = SLUG_MAX - suffix.length;
  const trimmed = (base.length > room ? base.slice(0, room) : base).replace(/-+$/, '');
  return trimmed === '' ? '' : `${trimmed}${suffix}`;
}

export interface SlugSeed {
  /** What to pre-fill the field with. */
  slug: string;
  /** Whether that value was PROVEN available — the caller uses this to
   *  enable Continue without a second round-trip. */
  available: boolean;
  /** How many availability calls were spent. 0 = never asked. */
  attempts: number;
}

export type SlugCheck = (slug: string) => Promise<{ available: boolean }>;

/**
 * Pre-fill the free-subdomain door: derive from the company name, and if
 * that name is taken, walk to the next available variant rather than
 * landing a stranger on a red "Already taken" before they've seen the
 * product.
 *
 * NEVER STRANDS. A company name that derives to something unusable (too
 * short, or nothing but punctuation) is pre-filled as-is with no network
 * call at all, and the field's own live check gives its normal advice; a
 * run of taken variants gives back the base with available:false, which is
 * exactly today's behaviour. Both leave the user typing, never blocked.
 *
 * A rejected check (offline, 429) resolves as "not available" for that
 * candidate rather than throwing — a seed is a convenience, and it must not
 * be able to break the step it is decorating.
 */
export async function pickAvailableSlug(
  companyName: string,
  check: SlugCheck,
  opts: { maxAttempts?: number } = {},
): Promise<SlugSeed> {
  // 5 candidates against a 60/min brake leaves plenty of headroom for the
  // live field's own debounced checks on the same IP.
  const maxAttempts = opts.maxAttempts ?? 5;
  const base = suggestSlug(companyName);
  if (!isSlugShaped(base)) return { slug: base, available: false, attempts: 0 };

  let attempts = 0;
  for (let n = 1; n <= maxAttempts; n += 1) {
    const candidate = n === 1 ? base : slugVariant(base, n);
    if (!isSlugShaped(candidate)) break;
    attempts += 1;
    let available = false;
    try {
      available = (await check(candidate)).available;
    } catch {
      available = false;
    }
    if (available) return { slug: candidate, available: true, attempts };
  }
  // Every candidate taken: hand back the honest base and let the field say so.
  return { slug: base, available: false, attempts };
}

// ---- Derived display fields -------------------------------------------------
// These are what "reflow" means in the stale-seed finding: both are pure
// functions of the slug, so once the slug reseeds they cannot disagree.

/** The free subdomain, as customers would visit it. */
export function portalSlugHost(slug: string): string {
  return `${normaliseSlug(slug) || 'your-fleet'}.portal.breakernetwork.app`;
}

/**
 * The buy door's worked example — "get ace-haulage.co.uk".
 *
 * Takes the COMPANY NAME, not the slug, and derives the clean slug itself.
 * That is the whole point of the signature: the `-N` availability variant is
 * a subdomain concern — it exists because someone else already holds that
 * portal address — and it says nothing about whether a DOMAIN is free. A
 * buy door reading "Get ace-haulage-2.co.uk" invents a worse name than the
 * one the user could actually have. The buy step's own live registrar search
 * is what shows real availability.
 *
 * Passing a slug in here is how that bug happened, so the parameter is no
 * longer a slug at all.
 *
 * Hyphens are kept — that is what the buy step searches for.
 */
export function buyExampleDomain(companyName: string): string {
  return `${suggestSlug(companyName) || 'yourfleet'}.co.uk`;
}

/** Field value → the form the server compares. */
export function normaliseSlug(slug: string): string {
  return slug.trim().toLowerCase();
}
